/* Gallery lookbook: links each numbered dot on a photo to its product card.
   Hovering either highlights both. Tapping a dot also brings its card into view when it is
   below the fold, which is how a phone gets from the photo to the product. */

if (!customElements.get('gallery-look')) {
  customElements.define('gallery-look', class GalleryLook extends HTMLElement {
    connectedCallback() {
      this.dots = Array.from(this.querySelectorAll('.gallery-lookbook__dot'));
      this.items = Array.from(this.querySelectorAll('.gallery-lookbook__item'));

      this.dots.forEach((dot) => {
        dot.addEventListener('mouseenter', () => this.activate(dot.dataset.index));
        dot.addEventListener('click', () => this.activate(dot.dataset.index, true));
      });

      this.items.forEach((item) => {
        item.addEventListener('mouseenter', () => this.activate(item.dataset.index));
        item.addEventListener('focusin', () => this.activate(item.dataset.index));
      });

      this.addEventListener('mouseleave', () => this.activate(null));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.activate(null);
      });
    }

    activate(index, reveal = false) {
      const current = index == null ? null : String(index);
      this.dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.index === current));
      this.items.forEach((item) => item.classList.toggle('is-active', item.dataset.index === current));

      // On a phone the list sits under the photo, so a tapped dot whose card is below the fold
      // brings that card just into view - the least scroll that shows it, never more.
      if (!reveal || current === null) return;
      const item = this.items.find((el) => el.dataset.index === current);
      if (!item) return;
      const box = item.getBoundingClientRect();
      if (box.top >= 0 && box.bottom <= window.innerHeight) return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      item.scrollIntoView({ block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
    }
  });
}

/* The room switcher: shows one room at a time and swaps it in place, so the page never scrolls.
   Thumbnails pick a room; the arrows, a swipe on the photo, or the arrow keys on the thumbnails
   step through, wrapping at the ends. The room is kept in the address as #room-N so a link opens on
   it - an id-free hash, so the browser has no element to scroll to. */

// The intro wraps naturally, as wide as its measure allows, unless that leaves a stub of a last
// line (under 35% of the first) - then it takes balanced lines instead. On a phone an intro that
// runs past two lines closes to two, with "Read More" under it. Rechecked on resize.
const PHONE = window.matchMedia('(max-width: 749px)');

function fitIntro(intro, more) {
  intro.classList.remove('is-clamped');
  intro.querySelectorAll('p').forEach((p) => {
    p.style.textWrap = '';
    const range = document.createRange();
    range.selectNodeContents(p);
    const rows = new Map();
    Array.from(range.getClientRects()).forEach((rect) => {
      const y = Math.round(rect.top);
      rows.set(y, (rows.get(y) || 0) + rect.width);
    });
    const widths = Array.from(rows.values());
    if (widths.length > 1 && widths[widths.length - 1] < widths[0] * 0.35) p.style.textWrap = 'balance';
  });

  if (!more) return;
  if (!PHONE.matches) {
    more.hidden = true;
    return;
  }
  const open = more.getAttribute('aria-expanded') === 'true';
  intro.classList.add('is-clamped');
  const first = intro.querySelector('p');
  const longer = intro.querySelectorAll('p').length > 1 || (first && first.scrollHeight > first.clientHeight + 1);
  if (!longer || open) intro.classList.remove('is-clamped');
  more.hidden = !longer;
}

// The gallery shows each photo cropped from the centre to 2996 x 1804, and dots are placed as
// percentages of that crop. Full screen shows the whole photo, so a dot's position is mapped back:
// a photo taller than the crop lost equal strips top and bottom, a wider one equal strips each side.
const CROP = 2996 / 1804;
function uncrop(x, y, aspect) {
  if (!aspect || Math.abs(aspect - CROP) < 0.001) return [x, y];
  if (aspect < CROP) {
    const kept = aspect / CROP;
    return [x, ((1 - kept) / 2) * 100 + y * kept];
  }
  const kept = CROP / aspect;
  return [((1 - kept) / 2) * 100 + x * kept, y];
}

if (!customElements.get('gallery-lookbook-viewer')) {
  customElements.define('gallery-lookbook-viewer', class GalleryLookbookViewer extends HTMLElement {
    connectedCallback() {
      this.looks = Array.from(this.querySelectorAll('.gallery-lookbook__look'));
      this.rooms = Array.from(this.querySelectorAll('.gallery-lookbook__room'));
      this.strip = this.querySelector('.gallery-lookbook__rooms');
      this.counters = Array.from(this.querySelectorAll('[data-current]'));

      const section = this.closest('.gallery-lookbook');
      const intro = section && section.querySelector('.gallery-lookbook__intro');
      if (intro) {
        const more = section.querySelector('.gallery-lookbook__more');
        if (more) {
          more.addEventListener('click', () => {
            const open = more.getAttribute('aria-expanded') !== 'true';
            more.setAttribute('aria-expanded', String(open));
            more.textContent = open ? more.dataset.less : more.dataset.more;
            intro.classList.toggle('is-clamped', !open);
          });
        }
        fitIntro(intro, more);
        if (document.fonts) document.fonts.ready.then(() => fitIntro(intro, more));
        let timer;
        this.onResize = () => {
          clearTimeout(timer);
          timer = setTimeout(() => fitIntro(intro, more), 150);
        };
        window.addEventListener('resize', this.onResize);
      }
      // Phones: the photo's dots wait behind a "Show products" button, and the choice holds from
      // room to room. In the theme editor they start shown, since that is where they are placed.
      this.dotToggles = Array.from(this.querySelectorAll('[data-dots-toggle]'));
      this.dotToggles.forEach((button) => {
        button.addEventListener('click', () => this.showDots(!this.classList.contains('is-dots-shown')));
      });
      if (window.Shopify && window.Shopify.designMode) this.showDots(true);

      this.markSolo(this.looks.find((look) => !look.hidden));
      this.current = Math.max(0, this.looks.findIndex((look) => !look.hidden));
      this.setUpFullScreen();
      if (this.looks.length < 2) return;

      this.rooms.forEach((room, i) => room.addEventListener('click', () => this.show(i)));
      this.querySelectorAll('[data-step]').forEach((button) => {
        button.addEventListener('click', () => this.show(this.current + Number(button.dataset.step)));
      });

      const nav = this.querySelector('.gallery-lookbook__nav');
      if (nav) {
        nav.addEventListener('keydown', (event) => {
          const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[event.key];
          if (!step || event.altKey || event.ctrlKey || event.metaKey) return;
          event.preventDefault();
          this.show(this.current + step);
          this.rooms[this.current].focus({ preventScroll: true });
        });
      }

      this.looks.forEach((look) => this.addSwipe(look.querySelector('.gallery-lookbook__photo')));
      if (this.dialog) this.addSwipe(this.dialog);

      // Theme editor: selecting a photo or hotspot block shows its room.
      this.onBlockSelect = (event) => {
        const look = event.target.closest && event.target.closest('.gallery-lookbook__look');
        if (look && this.contains(look)) this.show(this.looks.indexOf(look), false);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      const match = window.location.hash.match(/^#room-(\d+)$/);
      if (match) this.show(Number(match[1]) - 1, false);
      else this.preload(this.current);
    }

    disconnectedCallback() {
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    showDots(show) {
      this.classList.toggle('is-dots-shown', show);
      this.dotToggles.forEach((button) => { button.textContent = show ? 'Hide products' : 'Show products'; });
    }

    // Switching cross-fades with a view transition, held until the new photo is decoded
    // (at most 0.8s) so it never fades through the empty grey box. Browsers without view
    // transitions, and visitors who ask for reduced motion, get the instant swap.
    show(index, updateAddress = true) {
      const total = this.looks.length;
      const i = ((index % total) + total) % total;
      const changing = i !== this.current;
      this.current = i;

      const swap = () => {
        this.looks.forEach((look, k) => { look.hidden = k !== i; });
        this.rooms.forEach((room, k) => {
          if (k === i) room.setAttribute('aria-current', 'true');
          else room.removeAttribute('aria-current');
        });
        this.counters.forEach((counter) => { counter.textContent = i + 1; });
        this.markSolo(this.looks[i]);
        if (this.dialog && this.dialog.open) this.fillFullScreen(i);
      };

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (changing && updateAddress && document.startViewTransition && !reduced) {
        const settle = (el) => {
          if (!el) return null;
          const wait = new Promise((resolve) => { setTimeout(resolve, 800); });
          return el.decode ? Promise.race([el.decode().catch(() => {}), wait]) : wait;
        };
        const img = this.looks[i].querySelector('.gallery-lookbook__img');
        if (img) img.loading = 'eager';
        const fullScreen = this.dialog && this.dialog.open;
        const lookReady = fullScreen ? null : settle(img);
        // The whole screen cross-fades, not the gallery alone: a named element is lifted above the
        // page while it fades and would paint over the sticky header and the cookie banner. The class
        // scopes the 0.45s timing in gallery-lookbook.css to this switch only.
        const root = document.documentElement;
        const token = {};
        this.transition = token;
        root.classList.add('gallery-lookbook-fading');
        const transition = document.startViewTransition(async () => {
          swap();
          const ready = fullScreen ? settle(this.fullImg) : lookReady;
          if (ready) await ready;
        });
        transition.finished.finally(() => {
          if (this.transition === token) root.classList.remove('gallery-lookbook-fading');
        });
      } else {
        swap();
      }

      this.revealThumbnail(i);
      this.preload(i);
      if (updateAddress && !(window.Shopify && window.Shopify.designMode)) {
        history.replaceState(history.state, '', `#room-${i + 1}`);
      }
    }

    addSwipe(target) {
      target.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        this.touch = { x: touch.clientX, y: touch.clientY };
      }, { passive: true });
      target.addEventListener('touchend', (event) => {
        if (!this.touch || this.looks.length < 2) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touch.x;
        const dy = touch.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.current + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    // Full screen: a modal dialog showing the current room's photo alone, uncropped. Esc, the close
    // button or a click on the dark surround closes it; the arrow keys step through the rooms.
    setUpFullScreen() {
      this.dialog = this.querySelector('.gallery-lookbook__full');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') {
        this.querySelectorAll('[data-fullscreen]').forEach((button) => { button.hidden = true; });
        this.dialog = null;
        return;
      }
      this.fullImg = this.dialog.querySelector('.gallery-lookbook__full-img');
      this.fullFrame = this.dialog.querySelector('.gallery-lookbook__full-frame');
      this.fullDots = this.dialog.querySelector('.gallery-lookbook__full-dots');
      this.querySelectorAll('[data-fullscreen]').forEach((button) => {
        button.addEventListener('click', () => this.openFullScreen());
      });
      this.dialog.querySelector('[data-fullscreen-close]').addEventListener('click', () => this.dialog.close());
      // A click on the dark surround closes; anywhere else (bar the open card) closes the card.
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('.gallery-lookbook__pop')) return;
        if (event.target === this.dialog) this.dialog.close();
        else this.closePop();
      });
      // Esc closes an open card first, then the view.
      this.dialog.addEventListener('cancel', (event) => {
        if (!this.pop) return;
        event.preventDefault();
        this.closePop();
      });
      this.dialog.addEventListener('keydown', (event) => {
        const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
        if (!step || this.looks.length < 2) return;
        event.preventDefault();
        this.show(this.current + step);
      });
      this.dialog.addEventListener('close', () => {
        this.closePop();
        if (this.lockedScroll) document.body.classList.remove('overflow-hidden');
        this.lockedScroll = false;
      });
    }

    openFullScreen() {
      this.fillFullScreen(this.current);
      this.lockedScroll = !document.body.classList.contains('overflow-hidden');
      if (this.lockedScroll) document.body.classList.add('overflow-hidden');
      this.dialog.showModal();
    }

    // Same image as the gallery photo, asked for at screen width rather than column width, with
    // its dots moved from the gallery's crop onto the whole photo.
    fillFullScreen(i) {
      const look = this.looks[i];
      const photo = look && look.querySelector('.gallery-lookbook__img');
      if (!photo || !this.fullImg) return;
      const aspect = parseFloat(look.dataset.aspect) || (photo.naturalWidth / photo.naturalHeight) || CROP;
      this.fullFrame.style.setProperty('--ar', aspect);
      this.fullImg.sizes = '100vw';
      this.fullImg.srcset = photo.getAttribute('srcset') || '';
      this.fullImg.src = photo.getAttribute('src');
      this.fullImg.alt = photo.alt;

      this.closePop();
      const dots = Array.from(look.querySelectorAll('.gallery-lookbook__dot')).map((dot) => {
        const [x, y] = uncrop(parseFloat(dot.style.getPropertyValue('--x')), parseFloat(dot.style.getPropertyValue('--y')), aspect);
        const item = look.querySelector(`.gallery-lookbook__item[data-index="${dot.dataset.index}"]`);
        const name = item && item.querySelector('.gallery-lookbook__name');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'gallery-lookbook__dot';
        button.style.setProperty('--x', `${x}%`);
        button.style.setProperty('--y', `${y}%`);
        button.textContent = dot.textContent.trim();
        button.setAttribute('aria-label', name ? name.textContent.trim() : button.textContent);
        button.setAttribute('aria-expanded', 'false');
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          if (this.popDot === button) this.closePop();
          else this.openPop(button, item, x, y);
        });
        return button;
      });
      this.fullDots.replaceChildren(...dots);
    }

    // The product card from the list, copied next to the dot.
    openPop(dot, item, x, y) {
      this.closePop();
      const card = item && item.querySelector('.gallery-lookbook__card');
      if (!card) return;
      const pop = card.cloneNode(true);
      pop.classList.add('gallery-lookbook__pop');
      if (x >= 50) pop.classList.add('gallery-lookbook__pop--left');
      if (y >= 50) pop.classList.add('gallery-lookbook__pop--up');
      pop.style.left = `${x}%`;
      pop.style.top = `${y}%`;
      pop.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; });
      this.fullFrame.appendChild(pop);
      dot.classList.add('is-active');
      dot.setAttribute('aria-expanded', 'true');
      this.pop = pop;
      this.popDot = dot;
    }

    closePop() {
      if (this.pop) this.pop.remove();
      if (this.popDot) {
        this.popDot.classList.remove('is-active');
        this.popDot.setAttribute('aria-expanded', 'false');
      }
      this.pop = null;
      this.popDot = null;
    }

    // Scroll the thumbnail strip itself - never the page - so the current room's thumbnail shows.
    // Judged against where the strip is heading, not where it is, so quick back-and-forth clicks
    // can't leave an earlier smooth scroll carrying the current thumbnail out of view.
    revealThumbnail(i) {
      const room = this.rooms[i];
      if (!room || !this.strip) return;
      const vertical = getComputedStyle(this.strip).flexDirection === 'column';
      const strip = this.strip.getBoundingClientRect();
      const box = room.getBoundingClientRect();
      const scroll = vertical ? this.strip.scrollTop : this.strip.scrollLeft;
      const start = (vertical ? box.top - strip.top : box.left - strip.left) + scroll;
      const size = vertical ? box.height : box.width;
      const view = vertical ? strip.height : strip.width;
      const heading = this.stripTarget == null ? scroll : this.stripTarget;
      if (start >= heading && start + size <= heading + view) return;

      const to = Math.max(0, start - (view - size) / 2);
      this.stripTarget = to;
      clearTimeout(this.stripTimer);
      this.stripTimer = setTimeout(() => { this.stripTarget = null; }, 700);
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.strip.scrollTo({ [vertical ? 'top' : 'left']: to, behavior: smooth ? 'smooth' : 'auto' });
    }

    // A room with no live products hides the "In this room" label above it.
    markSolo(look) {
      this.classList.toggle('is-solo', Boolean(look && look.classList.contains('gallery-lookbook__look--solo')));
    }

    // Start loading the rooms either side, so stepping to them shows the photo straight away.
    preload(i) {
      const total = this.looks.length;
      [i - 1, i + 1].forEach((k) => {
        const look = this.looks[((k % total) + total) % total];
        look.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      });
    }
  });
}
